import React, { useState } from 'react';
import { ImageListItem, ImageList, Modal, Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { Close as CloseIcon, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

import gallery1 from './../../../assets/gallery/gallery-1.jpeg';
import gallery2 from './../../../assets/gallery/gallery-2.jpeg';
import gallery3 from './../../../assets/gallery/gallery-3.jpeg';
import gallery4 from './../../../assets/gallery/gallery-4.jpeg';
import gallery5 from './../../../assets/gallery/gallery-5.jpeg';
import gallery6 from './../../../assets/gallery/gallery-6.jpeg';
import gallery7 from './../../../assets/gallery/gallery-7.jpeg';
import gallery8 from './../../../assets/gallery/gallery-8.jpeg';
import gallery9 from './../../../assets/gallery/gallery-9.jpeg';
import gallery10 from './../../../assets/gallery/gallery-10.jpeg';
import gallery11 from './../../../assets/gallery/gallery-11.jpeg';
import gallery12 from './../../../assets/gallery/gallery-12.jpeg';
import gallery13 from './../../../assets/gallery/gallery-13.jpeg';
import gallery14 from './../../../assets/gallery/gallery-14.jpeg';
import gallery15 from './../../../assets/gallery/gallery-15.jpeg';
import gallery16 from './../../../assets/gallery/gallery-16.jpeg';
import gallery17 from './../../../assets/gallery/gallery-17.jpeg';
import gallery18 from './../../../assets/gallery/gallery-18.jpeg';
import gallery19 from './../../../assets/gallery/gallery-19.jpeg';

import './Gallery.module.css';

const photos = [
  {
    src: gallery1,
    width: 715,
    height: 1045,
  },
  {
    src: gallery2,
    width: 1536,
    height: 2048,
  },
  {
    src: gallery3,
    width: 1536,
    height: 2048,
  },
  {
    src: gallery4,
    width: 496,
    height: 372,
  },
  {
    src: gallery5,
    width: 1536,
    height: 2048,
  },
  {
    src: gallery6,
    width: 2048,
    height: 1536,
  },
  {
    src: gallery7,
    width: 1024,
    height: 577,
  },
  {
    src: gallery8,
    width: 2048,
    height: 1536,
  },
  {
    src: gallery9,
    width: 1536,
    height: 2048,
  },
  {
    src: gallery10,
    width: 2048,
    height: 1536,
  },
  {
    src: gallery11,
    width: 1536,
    height: 2048,
  },
  {
    src: gallery12,
    width: 1536,
    height: 2048,
  },
  {
    src: gallery13,
    width: 2048,
    height: 1536,
  },
  {
    src: gallery14,
    width: 1536,
    height: 2048,
  },
  {
    src: gallery15,
    width: 2048,
    height: 1536,
  },
  {
    src: gallery16,
    width: 2048,
    height: 1536,
  },
  {
    src: gallery17,
    width: 1536,
    height: 2048,
  },
  {
    src: gallery18,
    width: 1536,
    height: 2048,
  },
  {
    src: gallery19,
    width: 1536,
    height: 2048,
  },
];

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const theme = useTheme();
  const isXLarge = useMediaQuery(theme.breakpoints.up('xl')); // 1536px+
  const isLarge = useMediaQuery(theme.breakpoints.up('lg')); // 1200px+
  const isMedium = useMediaQuery(theme.breakpoints.up('md')); // 900px+
  const isSmall = useMediaQuery(theme.breakpoints.up('sm')); // 600px+
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px

  // Determine number of columns based on screen size
  const getColumns = () => {
    if (isXLarge) return 5; // Very wide screens
    if (isLarge) return 4; // Large screens
    if (isMedium) return 3; // Medium screens
    if (isSmall) return 2; // Small screens
    return 1; // Mobile
  };

  const handleImageClick = (photo, index) => {
    setSelectedImage(photo);
    setCurrentIndex(index);
  };

  const handleClose = () => {
    setSelectedImage(null);
  };

  const handlePrevImage = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : photos.length - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(photos[newIndex]);
  };

  const handleNextImage = () => {
    const newIndex = currentIndex < photos.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
    setSelectedImage(photos[newIndex]);
  };

  // Calculate optimal dimensions for modal image
  const getModalImageStyle = (image) => {
    if (!image) return {};
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const aspectRatio = image.width / image.height;
    
    // Reserve space for controls and padding
    const maxWidth = isMobile ? viewportWidth * 0.95 : viewportWidth * 0.9;
    const maxHeight = isMobile ? viewportHeight * 0.85 : viewportHeight * 0.9;
    
    let finalWidth, finalHeight;
    
    // Calculate dimensions while maintaining aspect ratio
    if (aspectRatio > maxWidth / maxHeight) {
      // Image is wider relative to viewport
      finalWidth = Math.min(maxWidth, image.width);
      finalHeight = finalWidth / aspectRatio;
    } else {
      // Image is taller relative to viewport
      finalHeight = Math.min(maxHeight, image.height);
      finalWidth = finalHeight * aspectRatio;
    }
    
    return {
      width: finalWidth,
      height: finalHeight,
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    };
  };

  return (
    <>
      <Box sx={{ 
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: { xs: 1, sm: 2, md: 3 } 
      }}>
        <ImageList 
          variant="masonry" 
          cols={getColumns()} 
          gap={8}
          sx={{
            margin: 0,
          }}
        >
          {photos.map((item, index) => (
            <ImageListItem 
              key={item.src} 
              onClick={() => handleImageClick(item, index)}
              sx={{ 
                cursor: 'pointer', 
                '&:hover': { 
                  opacity: 0.8,
                  transform: 'scale(1.02)',
                  transition: 'all 0.3s ease-in-out'
                },
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <img
                src={item.src}
                alt={`Gallery item ${index + 1}`}
                loading="lazy"
                style={{
                  borderRadius: '4px',
                }}
              />
            </ImageListItem>
          ))}
        </ImageList>
      </Box>

      {/* Full-screen modal */}
      <Modal
        open={!!selectedImage}
        onClose={handleClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          padding: isMobile ? 1 : 2,
        }}
        disableScrollLock
      >
        <Box 
          sx={{ 
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <AnimatePresence mode="wait">
            {selectedImage && (
              <motion.img
                key={currentIndex}
                src={selectedImage.src}
                alt={`Gallery item ${currentIndex + 1}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ 
                  duration: 0.3,
                  ease: "easeInOut"
                }}
                style={getModalImageStyle(selectedImage)}
              />
            )}
          </AnimatePresence>

          {/* Close button */}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: { xs: 8, sm: 16 },
              right: { xs: 8, sm: 16 },
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              '&:hover': { 
                bgcolor: 'rgba(0, 0, 0, 0.8)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease',
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Navigation arrows - only show if more than one image */}
          {photos.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: 'absolute',
                  left: { xs: 8, sm: 16 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  '&:hover': { 
                    bgcolor: 'rgba(0, 0, 0, 0.8)',
                    transform: 'translateY(-50%) scale(1.1)'
                  },
                  transition: 'all 0.2s ease',
                  zIndex: 1,
                }}
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: 'absolute',
                  right: { xs: 8, sm: 16 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  '&:hover': { 
                    bgcolor: 'rgba(0, 0, 0, 0.8)',
                    transform: 'translateY(-50%) scale(1.1)'
                  },
                  transition: 'all 0.2s ease',
                  zIndex: 1,
                }}
              >
                <ChevronRight />
              </IconButton>
            </>
          )}

          {/* Image counter */}
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: 16, sm: 24 },
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 1,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              zIndex: 1,
            }}
          >
            {currentIndex + 1} / {photos.length}
          </Box>
        </Box>
      </Modal>
    </>
  );
};
  
export default Gallery;
